// Client API frontend pour le suivi GPS Océan (oceansystem.com).
// Miroir des typedefs backend/src/types/gps.js.

const BASE = "https://support.gs-group.fr/api";
const tok = () => localStorage.getItem("nexo_token");
const h = () => ({ Authorization: "Bearer " + tok() });

/**
 * @typedef {"disponible"|"en_trajet"|"en_intervention"|"hors_service"} TechnicianGpsStatus
 */

/**
 * @typedef {Object} TechnicianPosition
 * @property {string} technician_id
 * @property {string} [ocean_device_id]
 * @property {string} name
 * @property {number} latitude
 * @property {number} longitude
 * @property {number} [speed]
 * @property {string} last_update
 * @property {string} [vehicle_plate]
 * @property {number} [fuel_level]
 * @property {TechnicianGpsStatus} status
 */

/**
 * @typedef {Object} DispatchCandidate
 * @property {string} technician_id
 * @property {string} name
 * @property {number} distance_km
 * @property {number} eta_minutes
 * @property {TechnicianGpsStatus} status
 */

/** @returns {Promise<{enabled:boolean, auth_mode:string, last_sync_at:string|null, tracked_count:number}>} */
async function getStatus() {
  return fetch(`${BASE}/ocean/status`, { headers: h() }).then(r => r.json());
}

/** @param {boolean} [all] admin uniquement — inclut aussi les véhicules masqués @returns {Promise<TechnicianPosition[]>} */
async function getTechnicians(all = false) {
  const d = await fetch(`${BASE}/ocean/technicians${all ? "?all=1" : ""}`, { headers: h() }).then(r => r.json());
  return Array.isArray(d) ? d : [];
}

/** @param {string} plate @param {boolean} hidden */
async function setVehicleHidden(plate, hidden) {
  const r = await fetch(`${BASE}/ocean/vehicles/${encodeURIComponent(plate)}/hidden`, {
    method: "PATCH",
    headers: { ...h(), "Content-Type": "application/json" },
    body: JSON.stringify({ hidden }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Erreur ${r.status}`);
  return d;
}

/**
 * @param {number} lat
 * @param {number} lng
 * @param {number} [limit]
 * @returns {Promise<DispatchCandidate[]>}
 */
async function getNearest(lat, lng, limit = 5) {
  const params = new URLSearchParams({ lat, lng, limit });
  const d = await fetch(`${BASE}/ocean/dispatch/nearest?${params}`, { headers: h() }).then(r => r.json());
  return Array.isArray(d) ? d : [];
}

/**
 * @param {string} interventionId
 * @param {number} [limit]
 * @returns {Promise<DispatchCandidate[]>}
 */
async function getNearestForIntervention(interventionId, limit = 5) {
  const d = await fetch(`${BASE}/ocean/dispatch/nearest/intervention/${interventionId}?limit=${limit}`, { headers: h() }).then(r => r.json());
  return Array.isArray(d) ? d : [];
}

/** @returns {Promise<{enabled:boolean, baseUrl:string, loginPath:string, positionsPath:string, username:string, hasPassword:boolean, source:string, updatedAt:string|null}>} */
async function getConfig() {
  return fetch(`${BASE}/ocean/config`, { headers: h() }).then(r => r.json());
}

/** @param {object} cfg {enabled, baseUrl, loginPath, positionsPath, username, password?} */
async function saveConfig(cfg) {
  const r = await fetch(`${BASE}/ocean/config`, {
    method: "POST",
    headers: { ...h(), "Content-Type": "application/json" },
    body: JSON.stringify(cfg),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Erreur ${r.status}`);
  return d;
}

/**
 * Parcours d'un véhicule sur une journée (rétention 30 jours côté serveur).
 * @param {string} plate
 * @param {string} date 'YYYY-MM-DD'
 * @returns {Promise<Array<{ocean_device_id:string, vehicle_plate:string, name:string, latitude:number, longitude:number, status:string, recorded_at:string}>>}
 */
async function getHistory(plate, date) {
  const params = new URLSearchParams({ plate, date });
  const d = await fetch(`${BASE}/ocean/history?${params}`, { headers: h() }).then(r => r.json());
  return Array.isArray(d) ? d : [];
}

/** @param {object} cfg mêmes champs que saveConfig, non persistés */
async function testConfig(cfg) {
  const r = await fetch(`${BASE}/ocean/config/test`, {
    method: "POST",
    headers: { ...h(), "Content-Type": "application/json" },
    body: JSON.stringify(cfg),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Erreur ${r.status}`);
  return d;
}

/** Relevé kilométrique automatique du lundi matin — état de la dernière synchro (admin). @returns {Promise<{lastRunMondayKey:string|null, lastResult:{synced:number,skipped:number,total:number,ranAt:string}|null}>} */
async function getKmSyncStatus() {
  return fetch(`${BASE}/ocean/km-sync/status`, { headers: h() }).then(r => r.json());
}

/** Force une synchro kilométrique immédiate, sans attendre lundi matin (admin). */
async function runKmSync() {
  const r = await fetch(`${BASE}/ocean/km-sync/run`, { method: "POST", headers: h() });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Erreur ${r.status}`);
  return d;
}

export const oceanApi = { getStatus, getTechnicians, setVehicleHidden, getNearest, getNearestForIntervention, getHistory, getConfig, saveConfig, testConfig, getKmSyncStatus, runKmSync };
